import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Activity, Database, Pill, FileText, GitCompare, 
  TrendingUp, Users, Microscope, Heart, Shield, Clock,
  ChevronRight, CheckCircle, ArrowRight, Sparkles, Brain,
  Zap, Target, Globe, Lock, BarChart3
} from 'lucide-react';
import axios from 'axios';
import '@/LabsStyles.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Labs = () => {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [isChecking, setIsChecking] = useState(true);

  // Check if user is already authenticated - redirect to dashboard
  useEffect(() => {
    const checkExistingAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        if (response.data && response.data.email) {
          // User is already authenticated, redirect to dashboard
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch (error) {
        // Not authenticated, show landing page
      }
      setIsChecking(false);
    };

    checkExistingAuth();
  }, [navigate]);

  const handleJoinBeta = () => {
    navigate('/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to login with search query, then to dashboard after auth
      navigate('/login', { state: { searchQuery: searchQuery.trim() } });
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch(e);
    }
  };

  const features = [
    {
      icon: <Search size={40} />,
      title: 'AI Unified Search',
      description: 'Google-like medical search powered by retrieval + reasoning',
      details: 'Ask complex medical questions and get structured, evidence-based answers'
    },
    {
      icon: <Database size={40} />,
      title: 'Disease Intelligence',
      description: 'Comprehensive disease profiles with AI-powered insights',
      details: 'From etiology to treatment guidelines, all in one place'
    },
    {
      icon: <Pill size={40} />,
      title: 'Drug Intelligence',
      description: 'Complete drug information with interactions & alternatives',
      details: 'Mechanism of action, dosing, side effects, and cost comparisons'
    },
    {
      icon: <FileText size={40} />,
      title: 'Clinical Trials',
      description: 'Real-time trial search with AI-generated summaries',
      details: 'Compare methodologies, endpoints, and results instantly'
    },
    {
      icon: <GitCompare size={40} />,
      title: 'Guideline Comparison',
      description: 'Side-by-side comparison of global clinical guidelines',
      details: 'WHO vs CDC vs NICE vs ICMR - see key differences highlighted'
    },
    {
      icon: <TrendingUp size={40} />,
      title: 'Epidemiology Intelligence',
      description: 'Interactive maps and real-time disease trends',
      details: 'Prevalence, incidence, mortality, and outbreak alerts'
    }
  ];

  const useCases = [
    {
      title: 'For Clinicians',
      icon: <Activity size={32} />,
      benefits: ['Faster treatment decisions', 'Evidence-based care', 'Reduced research time']
    },
    {
      title: 'For Researchers',
      icon: <Microscope size={32} />,
      benefits: ['90% faster literature review', 'Automated insights', 'Bias detection']
    },
    {
      title: 'For Patients',
      icon: <Heart size={32} />,
      benefits: ['Personal health vault', 'Predictive analysis', 'Preventive care']
    },
    {
      title: 'For Pharma',
      icon: <Shield size={32} />,
      benefits: ['Competitive intelligence', 'Trial landscape', 'Market insights']
    }
  ];

  const patientFeatures = [
    {
      icon: <Database size={28} />,
      title: 'Personal Health Record Vault',
      description: 'Upload and organize all your medical records with AI auto-extraction'
    },
    {
      icon: <Brain size={28} />,
      title: 'Predictive Health Analysis',
      description: 'AI predicts disease progression and risk scores based on your data'
    },
    {
      icon: <Shield size={28} />,
      title: 'Preventive Care Engine',
      description: 'Personalized preventive roadmap with screening schedules'
    },
    {
      icon: <Activity size={28} />,
      title: 'Lifestyle Intelligence',
      description: 'Track sleep, exercise, diet with AI-powered habit corrections'
    },
    {
      icon: <Users size={28} />,
      title: 'Doctor Discovery',
      description: 'Find specialists and book consultations with AI-prepared summaries'
    },
    {
      icon: <BarChart3 size={28} />,
      title: 'Health Trend Visualizer',
      description: 'Graphical timelines for all your lab markers with AI explanations'
    }
  ];

  // Show loading while checking authentication
  if (isChecking) {
    return (
      <div className="labs-container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="labs-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">
            <Sparkles size={16} />
            <span>Now in Beta</span>
          </div>
          
          {/* Lab Flask Icon */}
          <div className="lab-flask-container">
            <svg viewBox="0 0 100 100" className="lab-flask">
              <path d="M35,10 L35,35 L20,70 C15,80 20,90 30,90 L70,90 C80,90 85,80 80,70 L65,35 L65,10" 
                    stroke="#667eea" strokeWidth="3" fill="rgba(102, 126, 234, 0.1)"/>
              <circle cx="50" cy="65" r="8" fill="#667eea" opacity="0.6">
                <animate attributeName="cy" values="65;55;65" dur="2s" repeatCount="indefinite"/>
              </circle>
              <circle cx="40" cy="70" r="5" fill="#764ba2" opacity="0.5">
                <animate attributeName="cy" values="70;60;70" dur="2.5s" repeatCount="indefinite"/>
              </circle>
              <circle cx="60" cy="68" r="6" fill="#667eea" opacity="0.4">
                <animate attributeName="cy" values="68;58;68" dur="3s" repeatCount="indefinite"/>
              </circle>
              <line x1="35" y1="10" x2="65" y2="10" stroke="#667eea" strokeWidth="4" strokeLinecap="round"/>
            </svg>
          </div>

          <h1 className="hero-title">
            MedPlexity <span className="gradient-text">Labs</span>
          </h1>
          <p className="hero-subtitle">
            Where Medical Intelligence Meets Innovation
          </p>
          
          <div className="hero-ctas">
            <button 
              className="cta-primary" 
              onClick={handleJoinBeta}
              data-testid="join-beta-hero"
            >
              Join the Beta <ArrowRight size={20} />
            </button>
            <button className="cta-secondary">
              Watch Demo <ChevronRight size={20} />
            </button>
          </div>

          {/* Functional Search Bar */}
          <form onSubmit={handleSearch} className="homepage-search-form">
            <div className="homepage-search-container">
              <Search size={22} className="homepage-search-icon" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search diseases, symptoms, or conditions..."
                className="homepage-search-input"
                aria-label="Search diseases"
              />
              <button 
                type="submit" 
                className="homepage-search-button"
                disabled={!searchQuery.trim()}
              >
                Search
              </button>
            </div>
            <p className="homepage-search-hint">
              Try: Diabetes, Hypertension, Chest Pain, COVID-19, Heart Disease...
            </p>
          </form>
        </div>
      </section>

      {/* Tagline Section */}
      <section className="tagline-section">
        <div className="section-content">
          <h2 className="tagline-title">AI-Powered Medical Intelligence. Faster Insights. Better Decisions.</h2>
          <p className="tagline-text">
            The single source of truth for medical evidence & research. 
            Search inside global medical knowledge — instantly.
          </p>
        </div>
      </section>

      {/* USP Section - Single Platform */}
      <section className="usp-highlight-section">
        <div className="section-content">
          <div className="usp-highlight-content">
            <div className="usp-highlight-text">
              <h2 className="usp-highlight-title">
                Single Platform to Cater to the Needs of 
                <span className="gradient-text"> Patients, Clinicians & Researchers</span>
              </h2>
              <p className="usp-highlight-description">
                MedPlexity Labs brings together three worlds under one roof — personal health management 
                for patients, clinical decision support for doctors, and comprehensive research tools for 
                medical researchers. One unified intelligence engine serving all healthcare stakeholders.
              </p>
              <button 
                className="cta-primary" 
                onClick={handleJoinBeta}
                data-testid="join-beta-usp"
              >
                Join the Beta <ArrowRight size={20} />
              </button>
            </div>
            
            <div className="usp-highlight-visual">
              <div className="usp-card-mini">
                <Heart size={32} color="#e91e63" />
                <h4>Patients</h4>
                <p>Personal health records & predictive analysis</p>
              </div>
              <div className="usp-card-mini">
                <Activity size={32} color="#2196f3" />
                <h4>Clinicians</h4>
                <p>Evidence-based clinical decision support</p>
              </div>
              <div className="usp-card-mini">
                <Microscope size={32} color="#9c27b0" />
                <h4>Researchers</h4>
                <p>Comprehensive medical research tools</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is MedPlexity */}
      <section className="what-section">
        <div className="section-content">
          <h2 className="section-title">What is MedPlexity?</h2>
          <p className="what-text">
            MedPlexity is an <strong>AI-driven medical data intelligence engine</strong> that aggregates 
            global research, guidelines, drug data, epidemiology, clinical evidence, and real-world 
            datasets — and transforms them into <strong>instant, actionable insights</strong>.
          </p>
        </div>
      </section>

      {/* Core Features */}
      <section className="features-section">
        <div className="section-content">
          <h2 className="section-title">Powerful Features</h2>
          <p className="section-subtitle">
            Everything you need for medical research and clinical decision-making
          </p>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`feature-card ${activeFeature === index ? 'active' : ''}`}
                onMouseEnter={() => setActiveFeature(index)}
                data-testid={`feature-card-${index}`}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                <p className="feature-details">{feature.details}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Patient Mode Section */}
      <section className="patient-section">
        <div className="section-content">
          <div className="patient-header">
            <Heart size={48} className="patient-icon" />
            <div>
              <h2 className="section-title">Personal Health Intelligence</h2>
              <p className="section-subtitle">
                AI-powered health management for patients
              </p>
            </div>
          </div>

          <div className="patient-features-grid">
            {patientFeatures.map((feature, index) => (
              <div key={index} className="patient-feature-card">
                <div className="patient-feature-icon">{feature.icon}</div>
                <h4 className="patient-feature-title">{feature.title}</h4>
                <p className="patient-feature-desc">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="patient-cta">
            <button 
              className="cta-primary" 
              onClick={handleJoinBeta}
              data-testid="join-beta-patient"
            >
              Join the Beta <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* USPs Section */}
      <section className="usps-section">
        <div className="section-content">
          <h2 className="section-title">Why MedPlexity?</h2>
          
          <div className="usps-grid">
            <div className="usp-card">
              <Target size={32} className="usp-icon" />
              <h3>Only Unified Medical Intelligence Engine</h3>
              <p>All global clinical, research, drug, and epidemiology data in one intelligent search</p>
            </div>

            <div className="usp-card">
              <CheckCircle size={32} className="usp-icon" />
              <h3>Evidence-Based Answers, Not AI Guesswork</h3>
              <p>Every answer includes source citations, guideline references, and research links</p>
            </div>

            <div className="usp-card">
              <GitCompare size={32} className="usp-icon" />
              <h3>Side-by-Side Comparative Intelligence</h3>
              <p>Compare diseases, treatments, drugs, guidelines, and trials in one click</p>
            </div>

            <div className="usp-card">
              <Clock size={32} className="usp-icon" />
              <h3>90% Faster Medical Research</h3>
              <p>One search replaces PubMed, UpToDate, Google Scholar, and trial registries</p>
            </div>

            <div className="usp-card">
              <Brain size={32} className="usp-icon" />
              <h3>Clinical Reasoning, Not Just Summaries</h3>
              <p>AI analyzes, interprets, and reasons like a clinical researcher</p>
            </div>

            <div className="usp-card">
              <Users size={32} className="usp-icon" />
              <h3>Built for Everyone</h3>
              <p>Doctors, researchers, pharma teams, students, and patients — one platform</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="use-cases-section">
        <div className="section-content">
          <h2 className="section-title">Who Uses MedPlexity?</h2>
          
          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="use-case-card">
                <div className="use-case-icon">{useCase.icon}</div>
                <h3 className="use-case-title">{useCase.title}</h3>
                <ul className="use-case-benefits">
                  {useCase.benefits.map((benefit, i) => (
                    <li key={i}>
                      <CheckCircle size={16} />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <div className="final-cta-content">
          <h2 className="final-cta-title">Ready to Transform Medical Intelligence?</h2>
          <p className="final-cta-subtitle">
            Join thousands of healthcare professionals already using MedPlexity
          </p>
          <button 
            className="cta-primary cta-large" 
            onClick={handleJoinBeta}
            data-testid="join-beta-footer"
          >
            Join the Beta <ArrowRight size={24} />
          </button>
          <p className="final-cta-note">
            <Lock size={16} /> Free during beta • No credit card required
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="labs-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <Activity size={32} />
            <div>
              <h3>MedPlexity Labs</h3>
              <p>AI-Powered Medical Intelligence Platform</p>
            </div>
          </div>
          <div className="footer-links">
            <div className="footer-column">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#use-cases">Use Cases</a>
              <a href="#pricing">Pricing</a>
            </div>
            <div className="footer-column">
              <h4>Company</h4>
              <a href="#about">About</a>
              <a href="#careers">Careers</a>
              <a href="#contact">Contact</a>
            </div>
            <div className="footer-column">
              <h4>Resources</h4>
              <a href="#docs">Documentation</a>
              <a href="#blog">Blog</a>
              <a href="#support">Support</a>
            </div>
            <div className="footer-column">
              <h4>Investors</h4>
              <a 
                href="https://github.com/yourusername/medplexity-pitch-deck" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="pitch-deck-link"
              >
                More Info 📊
              </a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2024 MedPlexity Labs. All rights reserved.</p>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <a 
              href="/admin" 
              className="footer-info-link"
              style={{ opacity: 0.6, fontSize: '12px' }}
            >
              Admin
            </a>
            <a 
              href="https://github.com/yourusername/medplexity-pitch-deck" 
              target="_blank" 
              rel="noopener noreferrer"
              className="footer-info-link"
            >
              More Info
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Labs;
